import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { FileText, Download, ChevronDown, CalendarRange, Info } from "lucide-react";
import API_BASE_URL from "../api";

// ✅ Same base your Dashboard already uses successfully.
const API_BASE = `${API_BASE_URL}/api/inputHistory`;

function toCSV(rows) {
  if (!rows.length) return "";
  const headers = Object.keys(rows[0]);
  const escape = (val) => `"${String(val ?? "").replace(/"/g, '""')}"`;
  const lines = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => escape(row[h])).join(",")),
  ];
  return lines.join("\n");
}

export default function AdminReports() {
  const [stats, setStats] = useState({
    totalCertificates: null,
    totalCarbonFootprint: null,
  });
  const [trendData, setTrendData] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Client-side "generated reports" — session only until a backend
  // persistence route exists. Each entry is a CSV built from live data.
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      setError(null);
      try {
        const [certRes, carbonRes, trendRes, recentRes] = await Promise.all([
          fetch(`${API_BASE}/totalCertificateGenerated`).then((r) => r.json()),
          fetch(`${API_BASE}/totalCarbonFootprintGenerated`).then((r) => r.json()),
          fetch(`${API_BASE}/monthlyCertificateTrend`).then((r) => r.json()),
          fetch(`${API_BASE}/recentCertificates`).then((r) => r.json()),
        ]);

        setStats({
          totalCertificates: certRes?.totalCertificate ?? 0,
          totalCarbonFootprint: carbonRes?.totalCarbonFootprintGenerated ?? 0,
        });
        setTrendData(trendRes?.trend || []);
        setCertificates(recentRes?.recentCertificates || []);
      } catch (err) {
        console.error("Failed to load report data:", err);
        setError("Couldn't load report data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  // Estimated CO₂ saved per month = (avg CO₂ per certificate) × (certificates that month).
  // This is a real average from your live totals, applied across the real monthly
  // certificate counts — replace with a true per-month backend aggregate when available.
  const avgCarbonPerCertificate =
    stats.totalCertificates && stats.totalCarbonFootprint
      ? stats.totalCarbonFootprint / stats.totalCertificates
      : 0;

  const chartData = useMemo(
    () =>
      trendData.map((row) => ({
        month: row.month,
        certificates: row.certificates,
        carbonSaved: Math.round((row.certificates || 0) * avgCarbonPerCertificate),
      })),
    [trendData, avgCarbonPerCertificate]
  );

  const handleGenerateReport = () => {
    if (!certificates.length) return;
    setGenerating(true);

    const csv = toCSV(
      certificates.map((c) => ({
        certificateNumber: c.certificateNumber,
        vehicleNumber: c.vehicleNumber,
        fuelType: c.fuelType,
        ownerName: c.ownerName,
        carbonFootprint: c.carbonFootprint,
        distance: c.distance,
        issueDate: c.issueDate,
        status: c.status,
      }))
    );

    const blob = new Blob([csv], { type: "text/csv" });
    const fileUrl = URL.createObjectURL(blob);
    const now = new Date();

    setReports((prev) => [
      {
        _id: now.getTime(),
        name: `Certificates Report — ${now.toLocaleDateString()}`,
        period: "Recent certificates",
        generatedOn: now.toISOString(),
        format: "CSV",
        fileUrl,
      },
      ...prev,
    ]);
    setGenerating(false);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Reports</h3>
          <p className="text-sm text-gray-500">Generated reports and monthly summaries</p>
        </div>
        <button
          onClick={handleGenerateReport}
          disabled={loading || generating || certificates.length === 0}
          className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors"
        >
          <FileText size={16} />
          {generating ? "Generating…" : "Generate New Report"}
        </button>
      </div>

      {error && (
        <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
          {error}
        </div>
      )}

      {/* Monthly summary chart */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-1">
          <h4 className="font-semibold text-gray-900">Certificates Issued vs CO₂ Saved</h4>
          <button className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
            <CalendarRange size={12} /> This Year <ChevronDown size={12} />
          </button>
        </div>
        <p className="text-xs text-gray-400 flex items-center gap-1 mb-3">
          <Info size={12} />
          Certificates are live monthly counts. CO₂ Saved is estimated using your average kg saved per certificate.
        </p>
        {loading ? (
          <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">
            Loading summary…
          </div>
        ) : chartData.length === 0 ? (
          <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">
            No report data yet
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0F0F0", fontSize: 12 }} />
              <Bar dataKey="certificates" fill="#2E7D32" radius={[6, 6, 0, 0]} name="Certificates" />
              <Bar dataKey="carbonSaved" fill="#F5A623" radius={[6, 6, 0, 0]} name="CO₂ Saved (kg, est.)" />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Generated reports list */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Generated Reports</h3>
          <span className="text-xs text-gray-400">Session only — add a backend route to persist these</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 text-xs">
                <th className="px-6 py-3 font-medium">Report Name</th>
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Generated On</th>
                <th className="px-4 py-3 font-medium">Format</th>
                <th className="px-6 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {reports.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
                    No reports generated yet — click "Generate New Report" above
                  </td>
                </tr>
              ) : (
                reports.map((row, i) => (
                  <tr
                    key={row._id}
                    className={i !== reports.length - 1 ? "border-b border-gray-50" : ""}
                  >
                    <td className="px-6 py-4 font-medium text-gray-800">{row.name}</td>
                    <td className="px-4 py-4 text-gray-600">{row.period}</td>
                    <td className="px-4 py-4 text-gray-500">
                      {new Date(row.generatedOn).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-gray-700 uppercase text-xs">{row.format}</td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-3 text-gray-400">
                        <a
                          href={row.fileUrl}
                          download={`${row.name}.csv`}
                          title="Download report"
                        >
                          <Download size={16} className="cursor-pointer hover:text-gray-600" />
                        </a>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// import React, { useEffect, useState } from "react";
// import {
//   BarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   ResponsiveContainer,
// } from "recharts";
// import { FileText, Download, ChevronDown, CalendarRange } from "lucide-react";
// import API_BASE_URL from "../api";

// // ⚠️ Adjust to match your real backend route once it exists.
// const API_BASE = `${API_BASE_URL}/api/reports`;

// export default function AdminReports() {
//   const [monthly, setMonthly] = useState([]);
//   const [chartLoading, setChartLoading] = useState(true);

//   const [reports, setReports] = useState([]);
//   const [reportsLoading, setReportsLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchChart = async () => {
//       setChartLoading(true);
//       try {
//         // ⚠️ Placeholder endpoint — replace with your real monthly-summary route.
//         const res = await fetch(`${API_BASE}/monthlySummary`).then((r) => r.json());
//         setMonthly(res?.summary || []);
//       } catch (err) {
//         console.error("Failed to load report summary:", err);
//       } finally {
//         setChartLoading(false);
//       }
//     };

//     const fetchReports = async () => {
//       setReportsLoading(true);
//       setError(null);
//       try {
//         // ⚠️ Placeholder endpoint — replace with your real "list generated reports" route.
//         const res = await fetch(`${API_BASE}/generatedReports`).then((r) => r.json());
//         setReports(res?.reports || []);
//       } catch (err) {
//         console.error("Failed to load generated reports:", err);
//         setError("Couldn't load reports. Please try again.");
//       } finally {
//         setReportsLoading(false);
//       }
//     };

//     fetchChart();
//     fetchReports();
//   }, []);

//   return (
//     <div className="space-y-5">
//       <div className="flex items-center justify-between flex-wrap gap-3">
//         <div>
//           <h3 className="text-lg font-bold text-gray-900">Reports</h3>
//           <p className="text-sm text-gray-500">Generated reports and monthly summaries</p>
//         </div>
//         <button className="flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium rounded-xl px-4 py-2.5 transition-colors">
//           <FileText size={16} />
//           Generate New Report
//         </button>
//       </div>

//       {/* Monthly summary chart */}
//       <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
//         <div className="flex items-center justify-between mb-4">
//           <h4 className="font-semibold text-gray-900">Certificates Issued vs CO₂ Saved</h4>
//           <button className="flex items-center gap-1 text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600">
//             <CalendarRange size={12} /> This Year <ChevronDown size={12} />
//           </button>
//         </div>
//         {chartLoading ? (
//           <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">
//             Loading summary…
//           </div>
//         ) : monthly.length === 0 ? (
//           <div className="h-[280px] flex items-center justify-center text-sm text-gray-400">
//             No report data yet
//           </div>
//         ) : (
//           <ResponsiveContainer width="100%" height={280}>
//             <BarChart data={monthly} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
//               <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
//               <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
//               <YAxis tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
//               <Tooltip
//                 contentStyle={{ borderRadius: 12, border: "1px solid #F0F0F0", fontSize: 12 }}
//               />
//               <Bar dataKey="certificates" fill="#2E7D32" radius={[6, 6, 0, 0]} name="Certificates" />
//               <Bar dataKey="carbonSaved" fill="#F5A623" radius={[6, 6, 0, 0]} name="CO₂ Saved (kg)" />
//             </BarChart>
//           </ResponsiveContainer>
//         )}
//       </div>

//       {error && (
//         <div className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-4 py-2">
//           {error}
//         </div>
//       )}

//       {/* Generated reports list */}
//       <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
//         <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
//           <h3 className="font-semibold text-gray-900">Generated Reports</h3>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="w-full text-sm">
//             <thead>
//               <tr className="text-left text-gray-400 text-xs">
//                 <th className="px-6 py-3 font-medium">Report Name</th>
//                 <th className="px-4 py-3 font-medium">Period</th>
//                 <th className="px-4 py-3 font-medium">Generated On</th>
//                 <th className="px-4 py-3 font-medium">Format</th>
//                 <th className="px-6 py-3 font-medium text-right">Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {reportsLoading ? (
//                 <tr>
//                   <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
//                     Loading reports…
//                   </td>
//                 </tr>
//               ) : reports.length === 0 ? (
//                 <tr>
//                   <td colSpan={5} className="px-6 py-8 text-center text-gray-400">
//                     No reports generated yet
//                   </td>
//                 </tr>
//               ) : (
//                 reports.map((row, i) => (
//                   <tr
//                     key={row._id || i}
//                     className={i !== reports.length - 1 ? "border-b border-gray-50" : ""}
//                   >
//                     <td className="px-6 py-4 font-medium text-gray-800">{row.name || "—"}</td>
//                     <td className="px-4 py-4 text-gray-600">{row.period || "—"}</td>
//                     <td className="px-4 py-4 text-gray-500">
//                       {row.generatedOn ? new Date(row.generatedOn).toLocaleDateString() : "—"}
//                     </td>
//                     <td className="px-4 py-4 text-gray-700 uppercase text-xs">{row.format || "PDF"}</td>
//                     <td className="px-6 py-4">
//                       <div className="flex items-center justify-end gap-3 text-gray-400">
//                         {row.fileUrl && (
//                           <a href={row.fileUrl} download title="Download report">
//                             <Download size={16} className="cursor-pointer hover:text-gray-600" />
//                           </a>
//                         )}
//                       </div>
//                     </td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// }